import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Smartphone, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fmtXof, apiError } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const OPERATORS = ["Orange Money", "MTN MoMo", "Wave"];

export default function Checkout() {
  const { items, clear, promoCode } = useCart();
  const { fmt, chargeCurrency, fmtCharge, code, rateText, updatedAt } = useCurrency();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [method, setMethod] = useState("mobile_money");
  const [operator, setOperator] = useState("Orange Money");
  const [phone, setPhone] = useState("");
  const [processing, setProcessing] = useState(false);
  const [methods, setMethods] = useState(null);
  const created = useRef(false);

  useEffect(() => {
    api.get("/payments/methods").then(({ data }) => setMethods(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (code !== "XOF") setMethod("card"); // Mobile Money disponible en FCFA uniquement
  }, [code]);

  useEffect(() => {
    if (created.current) return;
    if (items.length === 0) { navigate("/panier"); return; }
    created.current = true;
    api.post("/orders", {
      items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
      promo_code: promoCode || null,
    })
      .then(({ data }) => setOrder(data))
      .catch((e) => { toast.error(apiError(e, "Impossible de créer la commande")); navigate("/panier"); });
  }, []); // eslint-disable-line

  const payMobileMoney = async () => {
    if (phone.replace(/\D/g, "").length < 8) {
      toast.error("Numéro de téléphone invalide");
      return;
    }
    setProcessing(true);
    try {
      await api.post("/payments/mobile-money", { order_id: order.order_id, phone, operator });
      clear();
      toast.success("Paiement confirmé ! Vos produits sont disponibles.");
      navigate("/paiement/succes?order=" + order.order_id);
    } catch (e) {
      toast.error(apiError(e, "Paiement refusé"));
      setProcessing(false);
    }
  };

  const payCinetPay = async () => {
    if (phone.replace(/\D/g, "").length < 8) {
      toast.error("Numéro de téléphone invalide");
      return;
    }
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/cinetpay/init", {
        order_id: order.order_id, origin_url: window.location.origin, phone,
      });
      clear();
      window.location.href = data.payment_url;
    } catch (e) {
      toast.error(apiError(e, "Impossible d'initialiser CinetPay"));
      setProcessing(false);
    }
  };

  const payCard = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/payments/stripe/checkout", {
        order_id: order.order_id,
        origin_url: window.location.origin,
        currency: chargeCurrency,
      });
      clear();
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Impossible d'initialiser le paiement"));
      setProcessing(false);
    }
  };

  if (!order) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="animate-spin text-ciel" size={28} /></div>;
  }

  return (
    <div className="mx-auto max-w-5xl px-6 py-14" data-testid="checkout-page">
      <h1 className="font-display text-3xl font-bold tracking-tight text-navy">Paiement</h1>
      <p className="mt-2 text-sm text-slate-500">Commande <span className="font-mono font-semibold text-navy">{order.order_id}</span></p>

      <div className="mt-10 grid gap-8 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <div className={`grid gap-3 ${code === "XOF" ? "grid-cols-2" : "grid-cols-1"}`}>
            {code === "XOF" && (
              <button
                data-testid="method-mobile-money"
                onClick={() => setMethod("mobile_money")}
                className={`flex flex-col items-center gap-2 rounded-xl border-2 p-5 transition-colors duration-200 ${method === "mobile_money" ? "border-ciel bg-sky-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                <Smartphone size={24} className={method === "mobile_money" ? "text-ciel" : "text-slate-400"} />
                <span className="text-sm font-semibold text-navy">Mobile Money</span>
                <span className="text-[11px] text-slate-500">Orange Money CI · MTN MoMo CI · Wave — FCFA</span>
              </button>
            )}
            <button
              data-testid="method-card"
              onClick={() => setMethod("card")}
              className={`flex flex-col items-center gap-2 rounded-xl border-2 p-5 transition-colors duration-200 ${method === "card" ? "border-ciel bg-sky-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <CreditCard size={24} className={method === "card" ? "text-ciel" : "text-slate-400"} />
              <span className="text-sm font-semibold text-navy">
                {code === "XOF" ? "Carte bancaire" : "Carte bancaire internationale"}
              </span>
              <span className="text-[11px] text-slate-500">Visa · Mastercard{code !== "XOF" ? ` — débit en ${chargeCurrency}` : ""}</span>
            </button>
          </div>

          {code === "MAD" && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="mad-transfer-info">
              <p className="text-sm font-semibold text-navy">Virement international (Maroc)</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                Vous pouvez aussi régler par virement bancaire international. Référence à indiquer : <span className="font-mono font-semibold">{order.order_id}</span>.
                Contactez-nous après votre commande pour recevoir nos coordonnées bancaires ; l'accès est activé à réception des fonds.
              </p>
            </div>
          )}

          {methods?.cinetpay && (
            <button
              data-testid="method-cinetpay"
              onClick={() => setMethod("cinetpay")}
              className={`mt-3 flex w-full items-center justify-between rounded-xl border-2 p-4 transition-colors duration-200 ${method === "cinetpay" ? "border-orange-400 bg-orange-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <span className="flex items-center gap-3">
                <Smartphone size={20} className="text-orange-500" />
                <span className="text-left">
                  <span className="block text-sm font-semibold text-navy">CinetPay — Paiement réel</span>
                  <span className="block text-[11px] text-slate-500">Orange Money · MTN MoMo · Wave · Carte bancaire, en FCFA</span>
                </span>
              </span>
              <span className="rounded-full bg-orange-100 px-3 py-1 font-mono text-[10px] font-bold uppercase text-orange-600">Production</span>
            </button>
          )}

          {method === "mobile_money" ? (
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="mm-form">
              <label className="text-sm font-medium text-slate-700">Opérateur</label>
              <Select value={operator} onValueChange={setOperator}>
                <SelectTrigger data-testid="mm-operator" className="mt-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OPERATORS.map((op) => <SelectItem key={op} value={op} data-testid={`mm-op-${op}`}>{op}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="mt-5 block text-sm font-medium text-slate-700">Numéro de téléphone</label>
              <Input
                data-testid="mm-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Ex : 07 07 07 07 07"
                className="mt-2 font-mono"
              />
              <p className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-700">
                Mode démonstration : le paiement Mobile Money est simulé et confirmé automatiquement. Payable en FCFA uniquement — montant : <strong>{fmtXof(order.total)}</strong>.
              </p>
              <Button data-testid="mm-pay-btn" onClick={payMobileMoney} disabled={processing}
                className="mt-5 w-full rounded-full bg-ciel py-6 text-sm font-semibold text-white hover:bg-sky-700">
                {processing ? <><Loader2 className="mr-2 animate-spin" size={16} /> Confirmation en cours…</> : `Payer ${fmtXof(order.total)} par ${operator}`}
              </Button>
            </div>
          ) : method === "cinetpay" ? (
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="cinetpay-form">
              <label className="text-sm font-medium text-slate-700">Numéro de téléphone (Mobile Money)</label>
              <Input
                data-testid="cinetpay-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Ex : 07 07 07 07 07"
                className="mt-2 font-mono"
              />
              <p className="mt-3 text-xs leading-relaxed text-slate-500">
                Vous serez redirigé vers la page sécurisée CinetPay pour payer en FCFA par Orange Money, MTN MoMo, Wave ou carte bancaire.
              </p>
              <Button data-testid="cinetpay-pay-btn" onClick={payCinetPay} disabled={processing}
                className="mt-5 w-full rounded-full bg-orange-500 py-6 text-sm font-semibold text-white hover:bg-orange-600">
                {processing ? <><Loader2 className="mr-2 animate-spin" size={16} /> Redirection vers CinetPay…</> : `Payer ${fmt(order.total)} avec CinetPay`}
              </Button>
            </div>
          ) : (
            <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-testid="card-form">
              <p className="text-sm leading-relaxed text-slate-600">
                Paiement sécurisé par carte bancaire internationale (Visa / Mastercard).{" "}
                Montant exact débité : <strong className="text-navy">{fmtCharge(order.total)}</strong>
                {code !== "XOF" && <> (soit {fmtXof(order.total)} de référence)</>}.
              </p>
              {(code === "CNY" || code === "RUB") && (
                <p className="mt-2 rounded-lg bg-sky-50 p-3 text-xs font-medium text-ciel">Paiement traité en USD équivalent.</p>
              )}
              {code === "XOF" && (
                <p className="mt-2 text-xs text-slate-500">Conversion en EUR pour la carte bancaire (1 € = 655,957 FCFA). Le paiement CinetPay directement en FCFA sera disponible dès activation des clés marchandes.</p>
              )}
              <p className="mt-3 rounded-lg bg-sky-50 p-3 font-mono text-xs text-ciel">
                Carte de test : 4242 4242 4242 4242 · date future · CVC quelconque
              </p>
              <Button data-testid="card-pay-btn" onClick={payCard} disabled={processing}
                className="mt-5 w-full rounded-full bg-navy py-6 text-sm font-semibold text-white hover:bg-navy-light">
                {processing ? <><Loader2 className="mr-2 animate-spin" size={16} /> Redirection…</> : `Payer ${fmtCharge(order.total)} par carte`}
              </Button>
            </div>
          )}

          <p className="mt-6 flex items-center gap-2 text-xs text-slate-400">
            <ShieldCheck size={14} className="text-emerald-500" /> Paiement chiffré — accès à vos produits immédiatement après confirmation.
          </p>
        </div>

        <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2" data-testid="checkout-summary">
          <h2 className="font-display text-base font-semibold text-navy">Votre commande</h2>
          <ul className="mt-4 space-y-3">
            {order.items.map((i) => (
              <li key={i.product_id} className="flex justify-between gap-3 text-sm">
                <span className="text-slate-600">{i.title}{i.qty > 1 && <span className="font-mono text-xs text-slate-400"> ×{i.qty}</span>}</span>
                <span className="whitespace-nowrap font-mono text-navy">{fmt(i.base)}</span>
              </li>
            ))}
          </ul>
          <dl className="mt-5 space-y-2 border-t border-slate-200 pt-4 text-sm">
            <div className="flex justify-between text-slate-600"><dt>Sous-total</dt><dd className="font-mono">{fmt(order.subtotal)}</dd></div>
            {order.tier_discount > 0 && <div className="flex justify-between text-emerald-600"><dt>Remise quantitative</dt><dd className="font-mono">- {fmt(order.tier_discount)}</dd></div>}
            {order.promo_discount > 0 && <div className="flex justify-between text-emerald-600"><dt>Promo ({order.promo.code})</dt><dd className="font-mono">- {fmt(order.promo_discount)}</dd></div>}
            <div className="flex justify-between pt-2 font-display text-lg font-bold text-navy"><dt>Total ({code})</dt><dd data-testid="checkout-total">{fmt(order.total)}</dd></div>
            {code !== "XOF" && (
              <>
                <div className="flex justify-between text-slate-500"><dt>Équivalent FCFA (référence)</dt><dd className="font-mono">{fmtXof(order.total)}</dd></div>
                <div className="flex justify-between text-slate-500"><dt>Taux appliqué</dt><dd className="font-mono text-xs">{rateText}</dd></div>
                <p className="pt-1 text-[10px] italic leading-relaxed text-slate-400" data-testid="checkout-legal-note">
                  Le montant final débité peut légèrement varier selon le taux appliqué par votre banque.
                </p>
              </>
            )}
          </dl>
        </aside>
      </div>
    </div>
  );
}
